import {
  Bot,
  History,
  Loader2,
  MessageCircle,
  MessageCirclePlus,
  MessageCircleX,
  Minimize2,
  MoreHorizontal,
  Plus,
  Send
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ChatMessage } from 'server';
import stradaLogo from '../../assets/strada-logo.svg';
import { useEndChat } from '../api/useEndChat';
import { useGetChat } from '../api/useGetChat';
import { useGetHistory } from '../api/useGetHistory';
import { useSendMessage } from '../api/useSendMessage';
import { useStartChat } from '../api/useStartChat';
import { useAnonymousChat } from '../hooks/useAnonymousChat';
import { useUserToken } from '../hooks/useUserToken';
import { cn } from '../lib/utils';
import { sdkBridge } from '../sdk-bridge';
import { ChatConversation } from './ChatConversation';
import { Button } from './ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from './ui/dropdown-menu';
import { TypingIndicator } from './ui/typing-indicator';

type UserOption = {
  value: string;
  text: string;
};

type ChatInterfaceProps = {
  organizationId: string;
  assistantId: string;
  chatId: string;
  initialMessage?: ChatMessage;
  isStartingChat?: boolean;
  userToken: string | null;
  onNewChat: () => void;
  onEndChat: () => void;
  onViewList: () => void;
  onMinimize: () => void;
};

const ChatInterface = ({
  organizationId: _organizationId,
  assistantId: _assistantId,
  chatId,
  initialMessage,
  isStartingChat,
  userToken,
  onNewChat,
  onEndChat,
  onViewList,
  onMinimize
}: ChatInterfaceProps) => {
  const [isShowingInitialMessage, setIsShowingInitialMessage] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [userOptions, setUserOptions] = useState<UserOption[]>([]);
  const [localMessages, setLocalMessages] = useState<ChatMessage[]>([]);
  const hasLoadedChatData = useRef<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { mutateAsync: sendMessage, isPending } = useSendMessage(chatId);
  const { mutateAsync: endChat } = useEndChat(chatId);
  const { data: existingChat, isLoading: chatLoading } = useGetChat(chatId);

  const messages = localMessages.length > 0 ? localMessages : existingChat?.messages || [];
  const chat = existingChat?.chat;

  const parseUserOptions = (content: string | Array<unknown> | null | undefined): UserOption[] => {
    if (!content || typeof content !== 'string') {
      return [];
    }
    const optionsRegex = /<options_for_user_to_choose>([\s\S]*?)<\/options_for_user_to_choose>/g;
    const optionRegex = /<option(?:\s+value="([^"]+)")?>([^<]+)<\/option>/g;
    const options: UserOption[] = [];
    let match;
    while ((match = optionsRegex.exec(content)) !== null) {
      const optionsContent = match[1];
      let optionMatch;
      while ((optionMatch = optionRegex.exec(optionsContent)) !== null) {
        const optionValue = optionMatch[1] ? optionMatch[1].trim() : optionMatch[2].trim();
        const optionText = optionMatch[2].trim();
        options.push({
          value: optionValue,
          text: optionText
        });
      }
    }
    return options;
  };

  const removeUserOptions = (content: string | Array<unknown> | null | undefined): string => {
    if (!content || typeof content !== 'string') return '';
    return content.replace(/<options_for_user_to_choose>[\s\S]*?<\/options_for_user_to_choose>/g, '').trim();
  };

  const handleOptionSelect = async (option: UserOption) => {
    if (isPending) return;
    if (option.value === 'end_chat') {
      await endChat();
      onEndChat();
      return;
    }
    const userMessage: ChatMessage = {
      role: 'user',
      content: option.text
    };
    setInputValue('');
    setUserOptions([]);

    const response = await sendMessage({ message: userMessage, userToken });
    if ('content' in response.message) {
      const options = parseUserOptions(response.message.content);
      setUserOptions(options);
    }
  };

  const MAX_MESSAGE_LENGTH = 300;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputValue.trim() || isPending || inputValue.length > MAX_MESSAGE_LENGTH) return;
    const userMessage: ChatMessage = {
      role: 'user',
      content: inputValue
    };
    setInputValue('');

    const response = await sendMessage({ message: userMessage, userToken });
    if ('content' in response.message) {
      const options = parseUserOptions(response.message.content);
      setUserOptions(options);
    }
  };

  const handleEndChat = async () => {
    await endChat();
    onEndChat();
  };

  // Initialize with initialMessage if provided
  useEffect(() => {
    if (initialMessage && chatId && hasLoadedChatData.current !== chatId) {
      hasLoadedChatData.current = chatId;
      setLocalMessages([initialMessage]);
      setIsShowingInitialMessage(true);

      setTimeout(() => {
        if ('content' in initialMessage) {
          const options = parseUserOptions(initialMessage.content);
          setUserOptions(options);
        }
        setIsShowingInitialMessage(false);
      }, 800);
    }
  }, [initialMessage, chatId]);

  // Load messages from existing chat if available
  useEffect(() => {
    if (existingChat && chatId && !initialMessage) {
      const existingMessages = existingChat.messages;
      if (existingMessages && existingMessages.length > 0) {
        setLocalMessages(existingMessages);
        const lastAssistantMessage = [...existingMessages].reverse().find((msg) => msg.role === 'assistant');
        if (lastAssistantMessage && 'content' in lastAssistantMessage) {
          const options = parseUserOptions(lastAssistantMessage.content);
          setUserOptions(options);
        }
      }
    }
  }, [existingChat, chatId, initialMessage]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const agentName = 'Chat Assistant'; // TODO: Get from session/settings

  return (
    <>
      <div
        className="flex items-center justify-between p-4 text-white sm:rounded-t-2xl"
        style={{ backgroundColor: '#4127D4' }}
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 bg-white rounded-full flex items-center justify-center">
            <span className="text-black font-bold text-sm">C</span>
          </div>
          <span className="font-semibold">{agentName}</span>
        </div>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            onClick={onMinimize}
            className="h-8 w-8 text-white hover:bg-white/20"
          >
            <Minimize2 className="h-4 w-4" />
          </Button>
          {userToken && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:bg-white/20">
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={onNewChat}>
                  <MessageCirclePlus className="h-4 w-4" />
                  <span className="ml-2">Start a new chat</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleEndChat}>
                  <MessageCircleX className="h-4 w-4" />
                  <span className="ml-2">End chat</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onViewList}>
                  <History className="h-4 w-4" />
                  <span className="ml-2">View recent chats</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>
      <div className="flex flex-col h-[calc(100%-4rem)]">
        <div className="overflow-y-auto flex-1 min-h-0 p-4 space-y-4 bg-white">
          {(chatLoading || isStartingChat || isShowingInitialMessage) && (
            <div className="flex items-start justify-start">
              <TypingIndicator />
            </div>
          )}
          {!chatLoading && !isStartingChat && !isShowingInitialMessage && messages.length === 0 && (
            <div className="flex flex-col items-center justify-center h-full text-center text-gray-500">
              <Bot className="h-12 w-12 mb-4 text-gray-300" />
              <p className="text-sm">Start a conversation with your AI assistant</p>
            </div>
          )}
          {!chatLoading && !isStartingChat && !isShowingInitialMessage && messages.length > 0 && (
            <>
              <ChatConversation
                messages={messages.filter(
                  (msg) =>
                    msg.role !== 'tool' &&
                    !(msg.role === 'assistant' && (msg as unknown as { tool_calls?: unknown }).tool_calls)
                )}
                removeUserOptions={removeUserOptions}
                agentName={agentName}
              />
              {isPending && <TypingIndicator />}
            </>
          )}
          <div ref={messagesEndRef} />
        </div>
        <div className="p-3 bg-white sm:rounded-b-2xl pb-safe">
          {chat && !chat.terminatedAt && !chat.abandonedAt && !chat.escalatedAt ? (
            <>
              {userOptions.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-col gap-2">
                    {userOptions.map((option, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        size="sm"
                        onClick={() => handleOptionSelect(option)}
                        className="text-xs bg-white hover:bg-gray-50 border border-gray-200 text-gray-700 text-center justify-center h-auto py-2 px-3 rounded-full whitespace-normal break-words min-w-0 max-w-full shadow-sm"
                        disabled={isPending}
                      >
                        {option.text}
                      </Button>
                    ))}
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit} className="relative">
                {inputValue.length > MAX_MESSAGE_LENGTH && (
                  <div className="mb-2 text-xs text-right">
                    <span className="text-red-500">Message too long</span>
                  </div>
                )}
                <div className="relative bg-white rounded-2xl shadow-md border border-[rgba(65,39,212,0.3)] focus-within:ring-1 focus-within:ring-[rgba(65,39,212,0.5)]">
                  <input
                    type="text"
                    placeholder="Message..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (
                        e.key === 'Enter' &&
                        !e.shiftKey &&
                        !isPending &&
                        inputValue.length <= MAX_MESSAGE_LENGTH
                      ) {
                        e.preventDefault();
                        handleSubmit(e as unknown as React.FormEvent);
                      }
                    }}
                    className="w-full bg-transparent px-4 py-3 pr-12 text-base placeholder:text-gray-500 focus:outline-none rounded-2xl"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2">
                    <Button
                      type="submit"
                      size="icon"
                      className="h-8 w-8 text-white hover:opacity-90 rounded-full bg-[#4127D4]"
                      disabled={!inputValue.trim() || isPending || inputValue.length > MAX_MESSAGE_LENGTH}
                    >
                      {isPending ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Send className="h-4 w-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </>
          ) : (
            chat && (
              <div className="flex flex-col items-center justify-center py-3 gap-2 text-center">
                <p className="text-sm text-gray-500">
                  {chat.abandonedAt ? 'The chat session has ended.' : 'The conversation was ended'}
                </p>
                <Button
                  onClick={onNewChat}
                  size="sm"
                  className="bg-[#4127D4] hover:bg-[#4127D4] hover:opacity-90 text-white"
                >
                  Start New Chat
                </Button>
              </div>
            )
          )}
          <a
            href="https://www.getstrada.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-1 pt-3 opacity-70 hover:opacity-90 transition-opacity"
          >
            <span className="text-xs">Powered by</span>
            <img src={stradaLogo} alt="Strada" className="h-4" />
          </a>
        </div>
      </div>
    </>
  );
};

type ChatWidgetProps = {
  organizationId: string;
  assistantId: string;
  greeting?: string;
};

export const ChatWidget = ({ organizationId, assistantId, greeting }: ChatWidgetProps) => {
  const [chatId, setChatId] = useState<string | null>(null);
  const [initialMessage, setInitialMessage] = useState<ChatMessage | undefined>({
    role: 'assistant',
    content: greeting
  });
  const [viewMode, setViewMode] = useState<'list' | 'chat'>('chat');
  const hasInitialized = useRef(false);

  const { userToken, isLoading: isLoadingToken } = useUserToken();
  const { getStoredChatId, setStoredChatId, clearStoredChatId } = useAnonymousChat();
  const { mutateAsync: startChat, isPending: isStartingChat } = useStartChat();

  const handleChatSelect = (selectedChatId: string) => {
    setChatId(selectedChatId);
    setInitialMessage(undefined);
    setViewMode('chat');
  };

  const handleNewChat = async () => {
    const response = await startChat({
      organizationId,
      assistantId,
      greeting,
      userToken
    });
    setChatId(response.chatId);
    setInitialMessage(response.initialMessage);
    setViewMode('chat');

    // Store chatId in sessionStorage for anonymous users
    if (!userToken) {
      setStoredChatId(response.chatId);
    }
  };

  const handleEndChat = () => {
    setChatId(null);
    setViewMode('list');
    clearStoredChatId();
  };

  const handleViewList = () => {
    setViewMode('list');
  };

  const handleMinimize = () => {
    // Notify SDK to hide the chat iframe
    sdkBridge.sendEvent('strada:chat:minimize', {});
  };

  // Restore chatId from sessionStorage for anonymous users
  useEffect(() => {
    if (!hasInitialized.current && !isLoadingToken) {
      hasInitialized.current = true;

      // For anonymous users, try to restore from sessionStorage
      if (!userToken) {
        const storedChatId = getStoredChatId();
        if (storedChatId) {
          setChatId(storedChatId);
          setInitialMessage(undefined);
          return;
        }
      }

      // Otherwise start a new chat
      handleNewChat();
    }
  }, [isLoadingToken, userToken]);

  return (
    <div
      className={cn(
        'fixed z-50 bg-white shadow-2xl',
        'inset-0 h-[100dvh] pb-safe',
        'sm:inset-auto sm:bottom-4 sm:right-4 sm:h-[700px] sm:w-96 sm:rounded-2xl'
      )}
    >
      {viewMode === 'list' ? (
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between p-4 text-white sm:rounded-t-2xl bg-[#4127D4]">
            <Button
              variant="ghost"
              size="icon"
              onClick={handleMinimize}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <h1 className="font-semibold text-sm">Recent chats</h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleNewChat}
              className="h-8 w-8 text-white hover:bg-white/20"
            >
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 overflow-y-auto min-h-0">
            <RecentChats
              organizationId={organizationId}
              assistantId={assistantId}
              onChatSelect={handleChatSelect}
              onNewChat={handleNewChat}
            />
          </div>
        </div>
      ) : chatId ? (
        <ChatInterface
          key={chatId}
          organizationId={organizationId}
          assistantId={assistantId}
          chatId={chatId}
          initialMessage={initialMessage}
          isStartingChat={isStartingChat}
          userToken={userToken}
          onNewChat={handleNewChat}
          onEndChat={handleEndChat}
          onViewList={handleViewList}
          onMinimize={handleMinimize}
        />
      ) : (
        <div className="flex flex-col items-center justify-center h-full">
          <Loader2 className="h-8 w-8 animate-spin text-[#4127D4]" />
          <p className="mt-4 text-sm text-gray-600">Starting chat...</p>
        </div>
      )}
    </div>
  );
};

type RecentChatsProps = {
  organizationId: string;
  assistantId: string;
  onChatSelect: (chatId: string) => void;
  onNewChat: () => void;
};

const RecentChats = ({
  organizationId,
  assistantId: _assistantId,
  onChatSelect,
  onNewChat: _onNewChat
}: RecentChatsProps) => {
  const { userToken } = useUserToken();
  const { data: history, isLoading } = useGetHistory(organizationId, userToken);

  const getChatStatus = (chat: { terminatedAt?: number; escalatedAt?: number; abandonedAt?: number }) => {
    if (chat.terminatedAt || chat.escalatedAt || chat.abandonedAt) {
      return 'closed';
    }
    return 'open';
  };

  const getStatusColor = (status: string) => {
    return status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800';
  };

  const formatTimeAgo = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
    if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    }
    if (minutes > 2) {
      return `${minutes} minutes ago`;
    }
    return 'Just now';
  };

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="text-sm text-gray-500">Loading recent chats...</div>
      </div>
    );
  }

  if (!history?.chats?.length) {
    return (
      <div className="flex flex-col items-center justify-center p-8 text-center gap-4">
        <div className="h-16 w-16 bg-[#4127D4] rounded-full flex items-center justify-center">
          <MessageCircle className="h-8 w-8 text-white" />
        </div>
        <div className="space-y-2">
          <h3 className="font-semibold text-lg text-gray-900">No Recent Chats</h3>
          <p className="text-sm text-gray-600 max-w-sm">Your chat history will appear here</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2 p-4">
      {history.chats.map((chat) => {
        const status = getChatStatus(chat);
        const timeAgo = formatTimeAgo(chat.createdAt);
        return (
          <div key={chat.id}>
            <div
              className="flex items-center gap-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors"
              onClick={() => onChatSelect(chat.id)}
            >
              <div className="h-8 w-8 bg-[#4127D4] rounded-full flex items-center justify-center flex-shrink-0">
                <MessageCircle className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-gray-900 truncate">
                  {chat.summary || `Chat ${chat.id.slice(0, 8)}`}
                </div>
                <div className="text-xs text-gray-500 flex items-center gap-1">
                  <span>•</span>
                  <span>{timeAgo}</span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn('px-2 py-1 rounded-full text-xs font-medium', getStatusColor(status))}>
                  {status === 'open' ? 'Open' : 'Closed'}
                </span>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
